class SiteHeader extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    wrapper: ".shopify-section--header",
    search: {
      wrapper: "[data-search-wrapper]",
      button: "[data-search-button]",
      buttonLabel: "[data-search-button-label]",
      input: "[data-search-input]",
      clear: "[data-search-clear]"
    }
  };

  get _scrollUpThreshold() {
    return 50;
  }

  get _headerElementHeight() {
    return Math.round(this.offsetHeight);
  }

  get isSticky() {
    return this.getAttribute("is-sticky") === "true";
  }

  get isHidden() {
    return this.getAttribute("is-hidden") === "true";
  }

  connectedCallback() {
    this._saveHeaderHeight(this._headerElementHeight);

    if (this.isSticky) {
      this._initSticky();
    }

    this._initSearch();

    this._scrollTicking = false;
    window.addEventListener("scroll", this._onScroll.bind(this));
    window.addEventListener("resize", this._onResize.bind(this));
  }

  _onScroll() {
    if (!this._scrollTicking) {
      this._scrollTicking = true;

      requestAnimationFrame(() => {
        this._onScrollHandler();
        this._scrollTicking = false;
      });
    }
  }

  _onScrollHandler() {
    this._saveHeaderHeight(this._headerElementHeight);

    if (!this.isSticky) {
      return;
    }

    const currentScrollPosition = Math.max(0, window.scrollY);
    const scrollDelta = currentScrollPosition - this._lastScrollPosition;

    const shouldWrapperBeSticky = currentScrollPosition > this._wrapperElementBottomPosition;
    const isWrapperSticky = this._wrapperElement.getAttribute("is-sticky") === "true";

    if (currentScrollPosition < this._wrapperElementTopPosition) {
      this._scrolledDistance = 0;
      this._show();

      this._wrapperElement.setAttribute("is-sticky", "false");

      return;
    }

    if (scrollDelta < 0) {
      this._scrolledDistance = 0;
      this._show();

      if (shouldWrapperBeSticky && !isWrapperSticky) {
        this._wrapperElement.setAttribute("is-sticky", "true");
      }
    } else if (currentScrollPosition > this._wrapperElementBottomPosition) {
      this._scrolledDistance += scrollDelta;

      if (this._scrolledDistance > this._scrollUpThreshold) {
        this._hide();
      }

      if (!shouldWrapperBeSticky && isWrapperSticky) {
        this._wrapperElement.setAttribute("is-sticky", "false");
      }
    }

    this._lastScrollPosition = currentScrollPosition;
  }

  _onResize() {
    this._saveHeaderHeight(this._headerElementHeight);

    if (this.isSticky) {
      this._wrapperElementTopPosition = this._wrapperElement.offsetTop;
      this._wrapperElementBottomPosition = this._wrapperElement.offsetTop + this._wrapperElement.offsetHeight;
    }
  }

  _initSticky() {
    this._lastScrollPosition = 0;
    this._scrolledDistance = 0;

    this._wrapperElement = this.closest(this.selectors.wrapper);
    this._wrapperElementTopPosition = this._wrapperElement.offsetTop;
    this._wrapperElementBottomPosition = this._wrapperElement.offsetTop + this._wrapperElement.offsetHeight;
  }

  _initSearch() {
    this._searchWrapper = this.querySelector(this.selectors.search.wrapper);
    this._searchButton = this.querySelector(this.selectors.search.button);
    this._searchInput = this.querySelector(this.selectors.search.input);
    this._searchButtonLabel = this.querySelector(this.selectors.search.buttonLabel);
    this._searchClear = this.querySelector(this.selectors.search.clear);

    if (!this._searchWrapper) {
      return;
    }

    this._searchButton.addEventListener("click", this._onSearchButtonClick.bind(this));
    document.addEventListener("click", this._onOutsideSearchClick.bind(this));
    this._searchWrapper.addEventListener("keydown", this._onSearchKeydown.bind(this));
    this._searchWrapper.addEventListener("focusout", this._onSearchFocusout.bind(this));
    this._searchInput.addEventListener("input", this._updateSearchButtonLabel.bind(this));
    this._searchClear?.addEventListener("click", this._onSearchClearClick.bind(this));
  }

  _hasSearchQuery() {
    return this._searchInput.value.trim() !== "";
  }

  _updateSearchButtonLabel() {
    if (!this._searchButtonLabel) {
      return;
    }

    const isExpanded = this._searchButton.getAttribute("aria-expanded") === "true";
    const key = isExpanded && this._hasSearchQuery() ? "labelSubmit" : "labelOpen";
    this._searchButtonLabel.textContent = this._searchButton.dataset[key];
  }

  _onSearchClearClick() {
    this._searchInput.value = "";
    this._searchInput.focus();
    this._updateSearchButtonLabel();
  }

  _onSearchFocusout(event) {
    if (event.relatedTarget && !this._searchWrapper.contains(event.relatedTarget)) {
      this._searchButton.setAttribute("aria-expanded", "false");
      this._updateSearchButtonLabel();
    }
  }

  _onSearchKeydown(event) {
    if (event.key !== "Escape") {
      return;
    }

    this._searchButton.setAttribute("aria-expanded", "false");
    this._updateSearchButtonLabel();
    this._searchButton.focus();
  }

  _onSearchButtonClick(event) {
    event.stopPropagation();

    const isSearchButtonExpanded = this._searchButton.getAttribute("aria-expanded") === "true";

    if (isSearchButtonExpanded && this._hasSearchQuery()) {
      this._searchInput.form.requestSubmit();
      return;
    }

    this._searchButton.setAttribute("aria-expanded", !isSearchButtonExpanded);
    this._updateSearchButtonLabel();

    if (!isSearchButtonExpanded) {
      this._searchInput.focus();
    } else {
      this._searchInput.blur();
    }
  }

  _onOutsideSearchClick(event) {
    if (!this._searchWrapper.contains(event.target)) {
      this._searchButton.setAttribute("aria-expanded", "false");
      this._updateSearchButtonLabel();
      this._searchInput.blur();
    }
  }

  _show() {
    this.setAttribute("is-hidden", "false");

    document.documentElement.style.setProperty(
      '--header-visible',
      `0`
    );
  }

  _hide() {
    this.setAttribute("is-hidden", "true");

    document.documentElement.style.setProperty(
      '--header-visible',
      `1`
    );
  }

  _saveHeaderHeight(value) {
    document.documentElement.style.setProperty(
      '--header-height',
      `${value}px`
    );
  }
}

if (!customElements.get("site-header")) {
  customElements.define("site-header", SiteHeader, { extends: "header" });
}

class HeaderSidebar extends HTMLElement {
  selectors = {
    openButton: "[data-header-sidebar-open]",
    closeButton: "[data-header-sidebar-close]",
    scrollableContent: "[data-scrollable]",
    tieredMenu: {
      container: "[data-tiered-menu]",
      openButton: "[data-tiered-menu-trigger]",
      backButton: "[data-tiered-menu-back-button]"
    }
  };

  modifiers = {
    sidebar: {
      open: "drawer--open"
    },
    tieredMenu: {
      open: "header-sidebar__navigation-tier--open"
    }
  };

  constructor() {
    super();
  }

  connectedCallback() {
    this._saveDrawerHeight(window.innerHeight);

    this._scrollableContent = this.querySelector(this.selectors.scrollableContent);

    this._initNavigation();
    this._initTieredMenus();

    document.addEventListener("navigation:open", this._openNavigation.bind(this));
    document.addEventListener("navigation:close", this._closeNavigation.bind(this));

    window.addEventListener("resize", throttle(this._onResize.bind(this)));
  }

  _onResize() {
    this._saveDrawerHeight(window.innerHeight);
  }

  _initNavigation() {
    document.addEventListener("click", (event) => {
      const openButton = event.target.closest(this.selectors.openButton);
      if (openButton) {
        const controls = openButton.getAttribute("aria-controls");
        if (controls && controls !== this.id) {
          return;
        }
        return this._openNavigation(event);
      }

      const closeButton = event.target.closest(this.selectors.closeButton);
      if (closeButton) {
        if (!this.contains(closeButton)) {
          return;
        }
        return this._closeNavigation(event);
      }
    });
  }

  _openNavigation(event) {
    event.preventDefault();

    this.setAttribute("aria-expanded", "true");
    this.removeAttribute("inert");

    bodyScrollLock.disableBodyScroll(this._scrollableContent);

    this.classList.add(this.modifiers.sidebar.open);
    document.body.classList.add("mobile-nav-open");
  }

  _closeNavigation(event) {
    event.preventDefault();

    this.setAttribute("aria-expanded", "false");
    this.setAttribute("inert", "");

    this.classList.remove(this.modifiers.sidebar.open);
    document.body.classList.remove("mobile-nav-open");

    this._closeAllTieredMenus();

    bodyScrollLock.enableBodyScroll(this._scrollableContent);
  }

  _initTieredMenus() {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        this._openTieredMenu(event);
      }
    });

    document.addEventListener("click", (event) => {
      const tieredMenuOpenButton = event.target.closest(this.selectors.tieredMenu.openButton);
      if (tieredMenuOpenButton) {
        return this._openTieredMenu(event);
      }

      const tieredMenuBackButton = event.target.closest(this.selectors.tieredMenu.backButton);
      if (tieredMenuBackButton) {
        return this._closeTieredMenu(event);
      }
    });
  }

  _openTieredMenu(event) {
    const openButton = event?.target.closest(this.selectors.tieredMenu.openButton);

    if (!openButton || !this.contains(openButton)) {
      return;
    }

    const tieredMenu = openButton.nextElementSibling;

    if (!tieredMenu) {
      return;
    }

    bodyScrollLock.disableBodyScroll(tieredMenu);

    tieredMenu.classList.add(this.modifiers.tieredMenu.open);

    openButton.setAttribute("aria-expanded", "true");
    tieredMenu.removeAttribute("inert");
  }

  _closeTieredMenu(event) {
    const backButton = event?.target.closest(this.selectors.tieredMenu.backButton);

    if (!backButton || !this.contains(backButton)) {
      return;
    }

    const tieredMenu = backButton.closest(this.selectors.tieredMenu.container);

    if (!tieredMenu) {
      return;
    }

    tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

    bodyScrollLock.enableBodyScroll(tieredMenu);

    const openButton = tieredMenu.previousElementSibling;
    if (openButton) {
      openButton.setAttribute("aria-expanded", "false");
    }
    tieredMenu.setAttribute("inert", "");
  }

  _closeAllTieredMenus() {
    const tieredMenus = [
      ...this.querySelectorAll(this.selectors.tieredMenu.container)
    ];

    if (!tieredMenus.length) {
      return false;
    }

    tieredMenus.forEach((tieredMenu) => {
      tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

      const openButton = tieredMenu.previousElementSibling;
      if (openButton) {
        openButton.setAttribute("aria-expanded", "false");
      }
      tieredMenu.setAttribute("inert", "");

      bodyScrollLock.enableBodyScroll(tieredMenu);
    });
  }

  _saveDrawerHeight(value) {
    this.style.setProperty(
      '--drawer-height',
      `${value}px`
    );
  }
}

if (!customElements.get("header-sidebar")) {
  customElements.define("header-sidebar", HeaderSidebar);
}
